/**
 * assignFields - Assigns values to fields in the data object
 * 
 * Supports:
 * - Direct boolean values: { 'fullyMatched': false }
 * - Path-based values resolved from data: { 'amountLeft': 'amountLeftOriginCurrency' }
 * 
 * @param {Object} data - The data object to process
 * @param {Object} operation - The operation configuration
 * @param {Object} prevData - Previous data (not used but kept for consistency)
 * @returns {Object} - The processed data with assigned fields
 */
import resolve from '../../utils/object/resolve.js';

const assignFields = (data, operation, prevData = {}) => {
    const { valueMap } = operation;
    
    if (!valueMap || !Array.isArray(valueMap)) {
        console.warn('[assignFields] Invalid valueMap: expected an array');
        return data;
    }
    
    let processedData = { ...data };
    
    for (const mapping of valueMap) {
        if (typeof mapping !== 'object' || mapping === null) {
            console.warn('[assignFields] Invalid mapping: expected an object');
            continue;
        }
        
        for (const [fieldName, fieldValue] of Object.entries(mapping)) {
            // Determine the value to assign
            let valueToAssign;
            
            if (typeof fieldValue === 'boolean') {
                // Direct boolean value
                valueToAssign = fieldValue;
            } else if (typeof fieldValue === 'number') {
                // Direct number value
                valueToAssign = fieldValue;
            } else if (typeof fieldValue === 'string') {
                // Check if it's a path to resolve from data
                // If the string starts with a letter and contains dots or is a simple field name,
                // try to resolve it from the data
                const resolvedValue = resolve(fieldValue, data);
                
                // If resolved value is not null, use it; otherwise use the string as-is
                // This allows for both path resolution and literal string values
                if (resolvedValue !== null && resolvedValue !== undefined) {
                    valueToAssign = resolvedValue;
                } else {
                    // If the string looks like a path (contains dots or brackets), 
                    // and resolves to null, keep as null
                    // Otherwise, use the literal string value
                    if (fieldValue.includes('.') || fieldValue.includes('[') || fieldValue.includes('/')) {
                        valueToAssign = null;
                    } else {
                        valueToAssign = fieldValue;
                    }
                }
            } else if (fieldValue === null) {
                // Explicit null
                valueToAssign = null;
            } else {
                // Other types (objects, arrays) - assign directly
                valueToAssign = fieldValue;
            }
            
            processedData[fieldName] = valueToAssign;
        }
    }
    
    return processedData;
};

export default assignFields;
